import { Tweet } from './tweetService';
import { callAI } from './aiService';
import { archiveAnalyzer, BuzzAnalysis } from './archiveAnalyzer';

// フック多様化：テンプレ固定を崩す（拡張版）
export const HOOK_VARIATIONS = {
  'question': [
    'Anyone else?', 'Is it just me?', 'Real talk:', 'Thoughts?', 'Am I wrong?', 'Right?',
    'Can anyone relate?', 'Who else?', 'Hot take or nah?', 'What do you think?', 'Unpopular opinion?',
    'Weird or normal?', 'Just me?', 'Does this track?', 'Too much?', 'Fair?'
  ],
  'conclusion': [
    'Takeaway:', 'Lesson learned:', 'Key insight:', 'Bottom line:', 'The answer:', 'TL;DR:',
    'In short:', 'To sum up:', 'Result:', 'The verdict:', 'Moral of the story:', 'Discovery:'
  ],
  'self-deprecation': [
    'I totally get it (I don\'t)', 'I might be done for', 'It\'s over', 'I messed up', 'Unwinnable',
    'RIP me', 'I\'m cooked', 'Down bad', 'Send help', 'Can\'t even', 'What have I done',
    'It\'s broken', 'Everything is fine (it\'s not)', 'Pain.'
  ],
  'empathy': [
    'So relatable', 'This.', 'Right?', 'Felt that', 'Same.',
    'Literally me', 'Exactly this', 'Fr fr', 'Say it louder', 'Facts', 'Couldn\'t agree more',
    'Big mood', 'Hard same', '100%'
  ],
  'observation': [
    'Just realized', 'Discovered', 'Found', 'TIL', 'Figured it out',
    'Turns out', 'Learned', 'It hit me', 'Just noticed', 'Clicked', 'It finally makes sense'
  ],
  'exclamation': [
    'Incredible', 'Wild', 'Insane', 'Absolutely nuts', 'Unreal',
    'No way', 'Sheesh', 'Wow', 'Bruh', 'Next level', 'Goated', 'Built different', 'Elite'
  ],
  'announcement': [
    'Update:', 'Breaking:', 'PSA:', 'Heads up:', 'Announcement:', 'Official:', 'Confirmed:', 'News:'
  ],
  'callout': [
    'Listen', 'Look at this', 'Wait', 'Hold on', 'Yo', 'Hey', 'Actually',
    'BTW', 'Speaking of which', 'Check this out', 'Hear me out', 'Stop scrolling'
  ],
};

export interface BuzzModel {
  insights: string[];
  analyzedTweetCount: number;
  thresholdEngagement: number;
  createdAt: string;
  // 追加：詳細分析結果
  detailedAnalysis?: BuzzAnalysis;
  bestPostingTime?: {
    bestHour: string;
    bestDay: string;
    confidence: number;
  };
}

export interface AnalyzeBuzzResponse {
  success: boolean;
  buzzModel: BuzzModel;
  message: string;
}

export const buzzService = {
  async analyzeBuzz(
    userId: string,
    tweets: Tweet[]
  ): Promise<AnalyzeBuzzResponse> {
    try {
      // 詳細分析を実行
      const detailedAnalysis = archiveAnalyzer.analyzeBuzzPatterns(tweets);
      const bestPostingTime = archiveAnalyzer.getBestPostingTime(detailedAnalysis);

      // Top20%のツイートを取得
      const topTweets = detailedAnalysis.topTweets.slice(0, 15);

      if (topTweets.length === 0) {
        throw new Error('No tweets with engagement found');
      }

      const topTweetTexts = topTweets
        .map(x => `[${x.engagement} engagement] ${x.text}`)
        .join('\n\n');

      // パターン分析結果をプロンプトに含める
      const patternSummary = this.generatePatternSummary(detailedAnalysis);

      // AI APIでバズパターン分析
      const analysisPrompt = `Below are the Top ${topTweets.length} highest-engagement tweets from a Twitter user.

# Viral Tweet Examples
${topTweetTexts}

# Data Analysis Results
${patternSummary}

# Task
Based on these, **extract 5 viral patterns/success factors that explain why engagement is high**. For each insight, briefly mention what "hook/structure/length/emoji & line breaks/CTA/posting time" contributed.

## Output Requirements
- Keep each line under 80 characters
- Make them actionable (e.g., "Lead with a question + number", "Keep under 200 chars with 2 line breaks")
- Use numbers & symbols to pack key points (e.g., "emoji≤2" "1-2 line breaks")
- Numbered list only. No preamble or closing remarks.

## Output
1. [Insight 1]
2. [Insight 2]
3. [Insight 3]
4. [Insight 4]
5. [Insight 5]`;

      const analysisText = await callAI(
        [
          { 
            role: 'system', 
            content: `You are an expert at analyzing viral tweets on Twitter. You leverage data analysis results to provide actionable insights.`
          },
          { role: 'user', content: analysisPrompt }
        ],
        { temperature: 0.65, maxTokens: 1000 }
      );

      const insights = this.parseInsights(analysisText);

      // インサイトが5つ未満の場合は数値分析から補完
      if (insights.length < 5) {
        const autoInsights = this.generateAutoInsights(detailedAnalysis);
        insights.push(...autoInsights.slice(0, 5 - insights.length));
      }

      const buzzModel: BuzzModel = {
        insights: insights.slice(0, 5),
        analyzedTweetCount: topTweets.length,
        thresholdEngagement: topTweets[topTweets.length - 1]?.engagement || 0,
        detailedAnalysis,
        bestPostingTime,
        createdAt: new Date().toISOString()
      };

      // localStorageに保存
      localStorage.setItem('buzz_model', JSON.stringify(buzzModel));

      return {
        success: true,
        buzzModel,
        message: 'Viral analysis complete'
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Viral analysis failed');
    }
  },

  // パターンサマリー生成
  generatePatternSummary(analysis: BuzzAnalysis): string {
    const p = analysis.patterns;
    const c = analysis.contentAnalysis;

    const topHashtags = c.topHashtags.slice(0, 3).map((h, i) => `${i + 1}. ${h.tag} (${h.count}x)`).join('\n');
    const toneDist = Object.entries(c.toneDistribution)
      .map(([tone, ratio]) => `${tone}: ${(ratio * 100).toFixed(0)}%`)
      .join(', ');

    let summary = `
## Pattern Analysis
- Contains question: ${(p.hasQuestion * 100).toFixed(0)}%
- Contains numbers: ${(p.hasNumbers * 100).toFixed(0)}%
- Contains emoji: ${(p.hasEmoji * 100).toFixed(0)}%
- Contains hashtag: ${(p.hasHashtag * 100).toFixed(0)}%
- Contains link: ${(p.hasLink * 100).toFixed(0)}%
- Contains line break: ${(p.hasLineBreak * 100).toFixed(0)}%
- Average length: ${p.avgLength.toFixed(0)} chars

## Engagement by Time of Day (Top 3)
${Object.entries(p.timePatterns)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 3)
  .map(([time, eng]) => `- ${time}: ${eng.toFixed(0)}`)
  .join('\n')}

## Engagement by Day of Week (Top 3)
${Object.entries(p.dayPatterns)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 3)
  .map(([day, eng]) => `- ${day}: ${eng.toFixed(0)}`)
  .join('\n')}

## Top Keywords (Top 5)
${c.topKeywords.slice(0, 5).map((k, i) => `${i + 1}. ${k.word} (${k.count}x)`).join('\n')}

## Hashtags (Top 3)
${topHashtags || 'None'}

## Tone Distribution
${toneDist}
`;

    return summary;
  },

  // 自動インサイト生成
  generateAutoInsights(analysis: BuzzAnalysis): string[] {
    const insights: string[] = [];
    const p = analysis.patterns;

    if (p.hasQuestion > 0.4) {
      insights.push('Tweets ending with a question tend to get more engagement');
    }
    if (p.hasNumbers > 0.5) {
      insights.push('Using specific numbers increases persuasiveness (e.g., 3 reasons)');
    }
    if (p.hasEmoji > 0.6) {
      insights.push('Moderate emoji use makes tweets more approachable (1-2 max)');
    }
    if (p.avgLength >= 120 && p.avgLength <= 200) {
      insights.push(`Tweets around ${Math.round(p.avgLength)} chars tend to get more reads`);
    }
    if (p.hasLineBreak > 0.3) {
      insights.push('Using line breaks for readability improves engagement');
    }
    if (p.hasHashtag > 0.3) {
      insights.push('Relevant hashtags increase discoverability');
    }

    // 時間帯分析
    const bestTime = Object.entries(p.timePatterns)
      .sort((a, b) => b[1] - a[1])[0];
    if (bestTime) {
      insights.push(`Posts at ${bestTime[0]} tend to get higher engagement`);
    }

    return insights;
  },

  // インサイトをパース
  parseInsights(analysisText: string): string[] {
    const lines = analysisText.split('\n').filter(line => line.trim());
    const insights: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (/^\d+\.\s/.test(trimmed) || /^[-・]/.test(trimmed)) {
        const insight = trimmed.replace(/^\d+\.\s*/, '').replace(/^[-・]\s*/, '').trim();
        if (!insight) continue;
        insights.push(insight.length > 60 ? insight.substring(0, 60) + '...' : insight);
      }
    }

    return insights;
  }
};
